import { twMerge } from 'tailwind-merge';
import { useMemo, useState, useEffect } from 'react';

import { CourseStepStatus } from 'types';
import { useCourseContext } from 'context';
import { getStepData, getStepFormFlags } from 'utils/steps';
import { useIsTabletUp, useIsTabletDown } from 'hooks/styles';

import { StepButtonProps } from './types';

export const StepButton = ({ item, onClick }: StepButtonProps) => {
  const [isHovered, setHovered] = useState(false);
  const { getStepStatus } = useCourseContext();
  const isTabletUp = useIsTabletUp();
  const isTabletDown = useIsTabletDown();

  const { id, color, codeName, formVersions, resourceVersions } = item;
  const {
    iconPath,
    showDivider,
    backgroundDark,
    backgroundLight70,
    backgroundLight90,
  } = useMemo(() => getStepData(color, codeName), [color, codeName]);

  const status = useMemo(() => getStepStatus(id), [getStepStatus, id]);

  const isResource = resourceVersions && resourceVersions.length > 0;
  const { isERP, isOrgPlan } = getStepFormFlags(formVersions, codeName);

  const isActive = status === CourseStepStatus.Active;
  const isNotActive = status !== CourseStepStatus.Active;
  const isCompleted = status === CourseStepStatus.Completed;

  const isClickable =
    isActive || ((isResource || isERP || isOrgPlan) && isCompleted);

  useEffect(() => {
    if (!isClickable && isHovered) {
      setHovered(false);
    }
  }, [isClickable, isHovered]);

  const handleClick = isClickable ? onClick : undefined;
  const handleMouseEnter = isClickable ? () => setHovered(true) : undefined;
  const handleMouseLeave = isClickable ? () => setHovered(false) : undefined;

  return (
    <div className="flex items-center">
      <div>
        {isTabletDown && isNotActive ? (
          <div
            className="h-3 w-3 rounded-xl"
            style={{
              background: isCompleted ? backgroundLight90 : backgroundLight70,
            }}
          >
            <div
              className="flex h-3 w-3 items-center justify-center rounded-xl"
              style={
                isCompleted
                  ? {
                      background: backgroundDark,
                      mask: 'url("/icons/check-circle.svg") no-repeat center / 8px 8px',
                      WebkitMask:
                        'url("/icons/check-circle.svg") no-repeat center / 8px 8px',
                    }
                  : {}
              }
            />
          </div>
        ) : (
          <div
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{ background: backgroundDark }}
            className={twMerge(
              'relative flex h-8 w-8 items-center justify-center rounded-full md:h-10 md:w-10 md:rounded-xl',
              isClickable && 'cursor-pointer',
              isHovered && 'opacity-60'
            )}
          >
            {(isTabletDown || isNotActive) && (
              <div
                style={{
                  background: backgroundLight70,
                }}
                className={
                  'absolute inset-[1px] rounded-full md:rounded-[11px]'
                }
              />
            )}
            <div
              className="h-5 w-5 md:h-6 md:w-6"
              style={{
                mask: `url("${iconPath}") no-repeat center / contain`,
                WebkitMask: `url("${iconPath}") no-repeat center / contain`,
                background: isTabletUp && isActive ? 'white' : backgroundDark,
              }}
            />
          </div>
        )}
      </div>
      {showDivider && isTabletUp && (
        <div className="bg-gray-divider ml-2 h-[35px] w-px lg:ml-3.5" />
      )}
    </div>
  );
};
